import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Popover,
  Modal,
  Avatar,
  Typography,
  Divider,
  Chip,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Plus, Buildings, CaretDown, CaretUp } from '@phosphor-icons/react';
import useThemeStyles from '../../theme/useThemeStyles';
import { getAvatarSrc } from '../../utils/avatarConstants';
import LegalDocumentModal from '../Legal/LegalDocumentModal';
import SupplierReferModal from './SupplierReferModal';
import api from '../../utils/api';
import { BusinessContext } from '../../context/BusinessContext';

const APP_VERSION = '0.1.0'; // From frontend/package.json

function ProfileMenuPopup({ anchorEl, open, onClose, userProfile, onLogout }) {
  const _styles = useThemeStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [legalDocuments, setLegalDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [referModalOpen, setReferModalOpen] = useState(false);
  const [businessDropdownOpen, setBusinessDropdownOpen] = useState(false);
  const [businessSubscription, setBusinessSubscription] = useState(null);
  
  // Get business context for suppliers (may be null if not in BusinessProvider)
  const isSupplier = userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier');
  const businessContext = useContext(BusinessContext);
  const businesses = businessContext?.businesses || [];
  const selectedBusiness = businessContext?.selectedBusiness;
  const selectBusiness = businessContext?.selectBusiness;
  const _refreshBusinesses = businessContext?.refreshBusinesses;
  const businessId = selectedBusiness?.business?._id;

  useEffect(() => {
    if (open) {
      loadLegalDocuments();
      if (isSupplier && businessId) {
        fetchBusinessSubscription();
      }
    } else {
      // Close business dropdown when popup closes
      setBusinessDropdownOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [open, businessId]);

  const loadLegalDocuments = async () => {
    try {
      const userType = userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier') ? 'supplier' : 'user';
      const response = await api.get('/api/legal', { params: { userType } });
      setLegalDocuments(response.data.documents || []);
    } catch (err) {}
  };

  const fetchBusinessSubscription = async () => {
    if (!businessId) return;
    try {
      const response = await api.get(`/api/business/${businessId}/subscriptions`);
      setBusinessSubscription(response.data);
    } catch (error) {
      // Subscription might not exist yet - that's okay
      setBusinessSubscription(null);
    }
  };

  const handleLegalDocClick = (doc) => {
    setSelectedDocument(doc);
    setModalOpen(true);
    onClose(); // Close the popup menu
  };

  const getPlanLabel = (membershipLevel) => {
    // Handle both old enum values and new configurable levels
    const levelMap = {
      'basic': 'Basic Plan',
      'premium': 'Premium Plan',
      'ultimate': 'Ultimate Plan',
      'bronze': 'Bronze Plan',
      'silver': 'Silver Plan',
      'gold': 'Gold Plan',
      'platinum': 'Platinum Plan',
      'free': 'Free Plan',
      'supplier_free': 'Free Plan'
    };
    
    if (membershipLevel && levelMap[membershipLevel.toLowerCase()]) {
      return levelMap[membershipLevel.toLowerCase()];
    }
    
    // If not in map, format the key nicely
    if (membershipLevel) {
      // Handle keys like "supplier_premium" by removing "supplier_" prefix
      const cleanedLevel = membershipLevel.replace(/^supplier_/i, '');
      // Split by underscore and capitalize each word
      const words = cleanedLevel.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      );
      return words.join(' ') + ' Plan';
    }
    
    return 'Free Plan';
  };

  const formatVersion = (version) => {
    // Convert "0.1.0" to "v0.1"
    const parts = version.split('.');
    return `v${parts[0]}.${parts[1]}`;
  };

  const handleMenuClick = (path) => {
    // Check if user is a supplier and adjust path accordingly
    const isSupplier = userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier');
    
    // If path doesn't start with /supplier and user is a supplier, add the prefix
    if (isSupplier && !path.startsWith('/supplier') && path !== '/settings' && path !== '/profile') {
      // For supplier-specific routes, keep as is
      // For general routes like /settings, keep as is (they'll work for both)
      navigate(path);
    } else {
      navigate(path);
    }
    onClose();
  };

  const handleLogout = () => {
    onLogout();
    onClose();
  };

  const handleOpenReferModal = () => {
    setReferModalOpen(true);
    onClose();
  };

  const handleBusinessSelect = (businessId) => {
    if (selectBusiness) {
      const selectedId = selectedBusiness?.business?._id != null ? String(selectedBusiness.business._id) : null;
      if (selectedId === businessId) {
        setBusinessDropdownOpen(false);
        return;
      }
      selectBusiness(businessId);
      setBusinessDropdownOpen(false);
      onClose();
      // Force a clean page-level refresh for the newly selected business context.
      window.location.assign('/supplier/dashboard');
    }
  };

  const toggleBusinessDropdown = () => {
    if (hasMultipleBusinesses) {
      setBusinessDropdownOpen(!businessDropdownOpen);
    }
  };

  const handleAddBusiness = () => {
    onClose();
    navigate('/supplier/register?add=1');
  };

  const displayName = userProfile?.firstName && userProfile?.lastName
    ? `${userProfile.firstName} ${userProfile.lastName}`
    : userProfile?.name || 'User';

  const displayEmail = userProfile?.email || '';
  
  const currentBusinessName = selectedBusiness?.business?.name || '';
  const hasMultipleBusinesses = businesses && businesses.length > 1;

  const popupContent = (
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Header Section - Expandable for multiple businesses */}
        <Box>
          <Box
            onClick={toggleBusinessDropdown}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              cursor: hasMultipleBusinesses ? 'pointer' : 'default',
              '&:hover': hasMultipleBusinesses ? {
                bgcolor: 'action.hover',
              } : {},
              transition: 'background-color 0.2s',
            }}
          >
            <Avatar
              src={getAvatarSrc(userProfile?.profilePicture || userProfile?.avatar)}
              alt={displayName}
              sx={{
                width: 56,
                height: 56,
                flexShrink: 0,
                backgroundColor: 'rgba(255, 255, 255, 1)',
              }}
            >
              {!userProfile?.profilePicture && !userProfile?.avatar && (
                displayName[0]?.toUpperCase() || 'U'
              )}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.1 }}>
                <Chip
                  label={getPlanLabel(
                    isSupplier && businessSubscription?.businessSubscription?.currentPlanMembershipLevelKey
                      ? businessSubscription.businessSubscription.currentPlanMembershipLevelKey
                      : userProfile?.membershipLevel
                  )}
                  size="small"
                  sx={{
                    bgcolor: '#FF6B35',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.5rem',
                    height: 18,
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.primary',
                }}
              >
                {isSupplier && selectedBusiness?.business?.name
                  ? selectedBusiness.business.name
                  : displayName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1,
                }}
              >
                {displayEmail}
              </Typography>
              
              {/* Current Business Info - Show when multiple businesses exist */}
              {isSupplier && hasMultipleBusinesses && selectedBusiness && (
                <Box
                  sx={{
                    mt: 1,
                    pt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Buildings size={12} weight="regular" />
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 500,
                      flex: 1,
                      fontSize: '0.75rem',
                    }}
                    noWrap
                  >
                    {currentBusinessName}
                  </Typography>
                  {businessDropdownOpen ? (
                    <CaretUp size={14} weight="bold" />
                  ) : (
                    <CaretDown size={14} weight="bold" />
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Business Dropdown - Expandable list */}
          {isSupplier && hasMultipleBusinesses && businessDropdownOpen && (
            <Box
              sx={{
                px: 2,
                pb: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.default',
              }}
            >
              {businesses.map((business) => {
                const id = business.business._id != null ? String(business.business._id) : '';
                const isSelected = id && selectedBusiness?.business?._id != null && String(selectedBusiness.business._id) === id;
                return (
                  <Box
                    key={id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBusinessSelect(id);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'action.selected' : 'transparent',
                      '&:hover': {
                        bgcolor: isSelected ? 'action.selected' : 'action.hover',
                      },
                      transition: 'background-color 0.2s',
                      mb: 0.5,
                    }}
                  >
                    <Buildings 
                      size={20} 
                      weight={isSelected ? "fill" : "duotone"} 
                      color={isSelected ? theme.palette.primary.main : undefined}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.primary',
                          fontWeight: isSelected ? 600 : 400,
                        }}
                        noWrap
                      >
                        {business.business.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                        noWrap
                      >
                        {business.role}
                      </Typography>
                    </Box>
                    {isSelected && (
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: 'primary.main',
                          color: 'white',
                        }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Add another business option - Only for suppliers */}
        {isSupplier && (
          <>
            <Box
              onClick={handleAddBusiness}
              sx={{
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Plus size={18} weight="bold" />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.primary',
                }}
              >
                Add another business
              </Typography>
            </Box>
            <Divider />
          </>
        )}

        <Divider />

        {/* Menu Items */}
        <Box sx={{ py: 1 }}>
          <Box
            onClick={() => {
              const isSupplier = userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier');
              handleMenuClick(isSupplier ? '/supplier/profile' : '/profile');
            }}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
              }}
            >
              {userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier') 
                ? 'Business Profile' 
                : 'Profile & Information'}
            </Typography>
          </Box>
          <Box
            onClick={() => {
              const isSupplier = userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier');
              handleMenuClick(isSupplier ? '/supplier/settings' : '/settings');
            }}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
              }}
            >
              Account Settings
            </Typography>
          </Box>
          <Box
            onClick={() => {
              // Check if user is a supplier and route accordingly
              const isSupplier = userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier');
              handleMenuClick(isSupplier ? '/supplier/billing' : '/billing');
            }}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
              }}
            >
              My Plans & Payments
            </Typography>
          </Box>
          
          {/* Show People for suppliers */}
          {(userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier')) && (
            <Box
              onClick={() => {
                handleMenuClick('/supplier/people');
              }}
              sx={{
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                }}
              >
                People
              </Typography>
            </Box>
          )}
          {(userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier')) && (
            <Box
              onClick={handleOpenReferModal}
              sx={{
                px: 2,
                py: 1.5,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: 'text.primary',
                }}
              >
                Invite my couples
              </Typography>
            </Box>
          )}
          <Box
            onClick={handleLogout}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'action.hover',
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: 'text.primary',
              }}
            >
              Logout
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {formatVersion(APP_VERSION)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {legalDocuments.slice(0, 3).map((doc) => (
              <MuiLink
                key={doc.fileName}
                component="button"
                onClick={() => handleLegalDocClick(doc)}
                sx={{
                  color: 'text.secondary',
                  fontSize: 'inherit',
                  textDecoration: 'none',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <Typography variant="caption" component="span">
                  {doc.buttonText}
                </Typography>
              </MuiLink>
            ))}
            {legalDocuments.length > 3 && (
              <MuiLink
                component={RouterLink}
                to={(userProfile?.role === 'supplier' || window.location.pathname.startsWith('/supplier')) 
                  ? '/supplier/settings' 
                  : '/settings'}
                onClick={onClose}
                sx={{
                  color: 'text.secondary',
                  fontSize: 'inherit',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                <Typography variant="caption" component="span">
                  More...
                </Typography>
              </MuiLink>
            )}
          </Box>
        </Box>
      </Box>
  );

  return (
    <>
      {isMobile ? (
        <Modal
          open={open}
          onClose={onClose}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '90%',
              maxWidth: 400,
              outline: 'none',
            }}
          >
            {popupContent}
          </Box>
        </Modal>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 320,
              maxWidth: 320,
              borderRadius: 1,
              boxShadow: 3,
              mt: 0.5,
              overflow: 'visible',
            },
          }}
        >
          {popupContent}
        </Popover>
      )}

      {/* Legal Document Modal */}
      {selectedDocument && (
        <LegalDocumentModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedDocument(null);
          }}
          fileName={selectedDocument.fileName}
          title={selectedDocument.title}
        />
      )}

      <SupplierReferModal
        open={referModalOpen}
        onClose={() => setReferModalOpen(false)}
        businessId={businessId}
        businessName={currentBusinessName}
      />
    </>
  );
}

export default ProfileMenuPopup;

